import { BoxSlotDetailsDtoSchema, type BoxSlotCreateOrUpdateInputDto } from "@/modules/boxSlot/gateway/boxSlotDto"
import { LoggerService } from "@modules/common/logger/loggerService"
import {
  BoxSessionDetailsDtoSchema,
  type BoxSessionDetailsDto,
} from "@/modules/boxSlotSession/gateway/boxSlotSessionDto"
import { SlotSubscriberDtoSchema, type SlotSubscriberDto } from "@/modules/profile/subscriber/gateway/slotSubscriberDto"
import { type BoxSlotDetailsDto } from "@modules/boxSlot/gateway/boxSlotDto"
import { ApiClient } from "@modules/core/api/client/apiClient"
import { type ParticipantStatus } from "@modules/profile/subscriber/subscriberTypes"
import { UserDtoSchema, type UserDto } from "@modules/profile/userProfile/userDto"
import { type Paginated } from "@modules/utils/paginated"
import dayjs from "dayjs"
import type { BoxSlotApiGateway } from "@/modules/boxSlot/gateway/boxSlotApiGateway"

export class BoxSlotApi implements BoxSlotApiGateway {
  private slotBaseUrl = "/boxes/slots"

  constructor(private api: ApiClient) {}

  async getSlotDetails(id: string): Promise<BoxSlotDetailsDto> {
    const resultDto = await this.api.get<BoxSlotDetailsDto>(`${this.slotBaseUrl}/${id}`)
    return LoggerService.parseApiDto(BoxSlotDetailsDtoSchema, resultDto.data)
  }

  async deleteSlot(id: string) {
    await this.api.delete(`${this.slotBaseUrl}/${id}`)
  }

  async getScheduledSessions(programId: string, startDate: Date, endDate: Date): Promise<BoxSessionDetailsDto[]> {
    const result = await this.api.get<BoxSessionDetailsDto[]>(`${this.slotBaseUrl}/fetch-scheduled-sessions`, {
      params: {
        startDate: dayjs(startDate).format("YYYY-MM-DD"),
        endDate: dayjs(endDate).format("YYYY-MM-DD"),
        programId,
      },
    })

    return LoggerService.parseApiDto(BoxSessionDetailsDtoSchema.array(), result.data)
  }

  async getScheduledSession(programId: string, date: Date): Promise<BoxSessionDetailsDto> {
    const sessionDto = await this.api.get<BoxSessionDetailsDto>(`${this.slotBaseUrl}/scheduled-session`, {
      params: {
        programId,
        date: dayjs(date).format("YYYY-MM-DD"),
      },
    })
    return LoggerService.parseApiDto(BoxSessionDetailsDtoSchema, sessionDto.data)
  }

  async createSlot(slot: BoxSlotCreateOrUpdateInputDto) {
    const slotDto = await this.api.post<BoxSlotDetailsDto>(`${this.slotBaseUrl}`, slot)
    return LoggerService.parseApiDto(BoxSlotDetailsDtoSchema, slotDto.data)
  }

  async updateSlot(id: string, slot: BoxSlotCreateOrUpdateInputDto) {
    const resultDto = await this.api.put<BoxSlotDetailsDto>(`${this.slotBaseUrl}/${id}`, slot)
    return LoggerService.parseApiDto(BoxSlotDetailsDtoSchema, resultDto.data)
  }

  async getSlotNonParticipants(slotId: string, name?: string) {
    const result = await this.api.get<Paginated<UserDto>>(`${this.slotBaseUrl}/${slotId}/non-participant-members`, {
      params: {
        page: 0,
        size: 10,
        name,
      },
    })

    return {
      ...result.data,
      content: result.data.content.map((member) => LoggerService.parseApiDto(UserDtoSchema, member)),
    }
  }

  async getSlotSubscribers(
    slotId: string,
    page?: number,
    size?: number,
    name?: string,
    participantStatus?: ParticipantStatus,
  ) {
    const result = await this.api.get<Paginated<SlotSubscriberDto>>(`${this.slotBaseUrl}/${slotId}/participants`, {
      params: {
        page,
        size,
        name,
        participantStatus,
      },
    })

    return {
      ...result.data,
      content: result.data.content.map((member) => LoggerService.parseApiDto(SlotSubscriberDtoSchema, member)),
    }
  }

  async addParticipantToSlot(slotId: string, memberId: string) {
    const result = await this.api.post<SlotSubscriberDto>(`${this.slotBaseUrl}/${slotId}/participants`, { memberId })
    return LoggerService.parseApiDto(SlotSubscriberDtoSchema, result.data)
  }

  async removeParticipantToSlot(slotId: string, memberId: string) {
    await this.api.delete(`${this.slotBaseUrl}/${slotId}/participants/${memberId}`)
  }

  async setParticipantAbsence(slotId: string, memberId: string, absent: boolean) {
    const result = await this.api.post<{ absent: boolean }>(
      `${this.slotBaseUrl}/${slotId}/participants/${memberId}/absence`,
      {
        absent,
      },
    )
    return result.data.absent
  }

  async duplicateWeekSessions(
    originBoxProgramId: string,
    destinationBoxProgramId: string,
    originStartDate: Date,
    originEndDate: Date,
    destinationStartDate: Date,
    overwrite: boolean = false,
  ) {
    await this.api.post(`${this.slotBaseUrl}/duplicate-box-sessions`, {
      programIdToDuplicateFrom: originBoxProgramId,
      programIdToDuplicateTo: destinationBoxProgramId,
      originStartDate: dayjs(originStartDate).format("YYYY-MM-DD"),
      originEndDate: dayjs(originEndDate).format("YYYY-MM-DD"),
      destinationStartDate: dayjs(destinationStartDate).format("YYYY-MM-DD"),
      overwrite,
    })
  }
}
